import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography, Select, Button, Spin, Upload, Input, InputNumber, message, Pagination, Table, Radio, Alert, Tag, Tooltip, Modal } from "antd";
import { UploadOutlined, InboxOutlined, DownloadOutlined, BellOutlined, PaperClipOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import "./FirmAdminReports.scss";
import "./ReportingCommon.scss";
import SidebarLayout from "../BankerUI/SidebarLayout";
import _NewapplicationService from "../../modules/NewapplicationService";
import _BankAdminService from "../../modules/BankAdminService";
import _ReportingService from "../../modules/ReportingService";
import { COMMON_ERROR_MSG, APP_BLOB_ROOT } from "../../constants/constants";
import {
  SENIOR_PRIVATE_BANKER_ROLE_ID,
  UNDERWRITER_BANKER_ROLE_ID,
  COMPLIANCE_ADMIN_ROLE_ID,
} from "../../constants/DataConstants";

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;
const { TextArea } = Input;

// Maps a Reporting Requirement's Document Type name to the backend's fixed sponsor-report upload
// field name -- same map the legacy Upload Document form already used, reused here so the new
// inline Submit panel calls the exact same upload code path (Requirement #4).
const DOCUMENT_TYPE_FIELD_NAME_MAP = {
  "Audited Financials": "sponserreport_auditedfinancials",
  "K-1": "sponserreport_k1",
  "Capital Call Notice": "sponserreport_capitalcallNotice",
  "Compliance Certificate": "sponserreport_compliancecertificate",
  "Quarterly Report": "sponserreport_quarterlyreport",
  "Other": "sponserreport_other",
};

const FUND_LEVEL_METRICS = ["IRR", "MOIC"];
const MANUAL_VALUE_METRICS = ["LTV", "DSCR", "Leverage"];

const CHIP_OPTIONS = [
  { key: "All", label: "All" },
  { key: "NeedsAction", label: "Needs action" },
  { key: "Overdue", label: "Overdue" },
];

const STATE_TAG_COLOR = {
  Upcoming: "blue",
  Overdue: "red",
  Pending: "orange",
  Tested: "default",
  Closed: "green",
};

const NewReportsPage = () => {
  const [loading, setLoading] = useState(false);

  // Identity -- same resolution the rest of the Banker UI already uses.
  const loginEmail = localStorage.getItem("loginEmail");
  const BankerId = localStorage.getItem("BankerId");
  const currentUserId = BankerId ? Number(BankerId) : null;
  const bankAdminIdRaw = localStorage.getItem("BankAdminId");
  const [BankAdminId, setBankAdminId] = useState(bankAdminIdRaw ? parseInt(bankAdminIdRaw) : null);
  const roleId = Number(localStorage.getItem("RoleId") ?? 0);
  const isFirmAdmin = JSON.parse(localStorage.getItem("IsFirmAdminFlag") || "false");
  const firmAdminApplicantId = localStorage.getItem("ApplicationId");
  const isSeniorPrivateBanker = roleId === SENIOR_PRIVATE_BANKER_ROLE_ID;
  const isBanker = isSeniorPrivateBanker; // matches the existing Firm/Facility-dropdown "isBanker" convention
  const canAct = isFirmAdmin
    || roleId === SENIOR_PRIVATE_BANKER_ROLE_ID
    || roleId === UNDERWRITER_BANKER_ROLE_ID
    || roleId === COMPLIANCE_ADMIN_ROLE_ID;

  // Scoped cycle list (Requirements #5/#6/#7).
  const [chip, setChip] = useState("All");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ AllCount: 0, NeedsActionCount: 0, OverdueCount: 0, FlaggedCount: 0 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const pageSize = 25;

  // Narrowing filters (banker-side screen only, per mockup 2) -- optional, default "All".
  const [firmList, setFirmList] = useState([]);
  const [narrowFirmId, setNarrowFirmId] = useState(undefined);
  const [facilityList, setFacilityList] = useState([]);
  const [narrowSponserId, setNarrowSponserId] = useState(undefined);

  // Facility/fund lookup for the Submit panel (Firm Admin) -- same source the legacy Upload
  // Document form already used.
  const [sponsorFacilities, setSponsorFacilities] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);

  const rowIdRef = useRef(1);
  const getNextRowId = () => rowIdRef.current++;

  // Inline panel state -- one row open at a time, no modals (Requirements #11/#12).
  const [expandedRow, setExpandedRow] = useState(null); // the full row object
  const [panelMode, setPanelMode] = useState(null); // "submit" | "verify" | "review" | "view"

  // Submit panel
  const [submitFundValues, setSubmitFundValues] = useState({}); // fundId -> string value
  const [submitManualValue, setSubmitManualValue] = useState(undefined);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitFileUrl, setSubmitFileUrl] = useState(null);
  // uploadRes.data.URL from the upload API is already a SAS-signed URL (see
  // UploadImagesService.UploadPdfOrDocFile) -- submitFileUrl above is deliberately stripped down to
  // a bare path (via `new URL(...).pathname`) because that's what DocumentPath must be saved as, but
  // that same stripped value can't be used as a clickable link -- it has no SAS signature, so Azure
  // Blob Storage rejects it with an authorization error. Keep the original signed URL separately, only
  // for the "view the file I just uploaded" link in this panel.
  const [submitFileSignedUrl, setSubmitFileSignedUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Verify panel
  const [verifyMetricResults, setVerifyMetricResults] = useState([]);
  const [institutionActualValues, setInstitutionActualValues] = useState({}); // metricResultId -> value
  const [verifying, setVerifying] = useState(false);

  // Review panel
  const [reviewComplianceStatus, setReviewComplianceStatus] = useState(undefined);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    const handleBankAdminIdChange = (e) => setBankAdminId(e.detail.newId);
    window.addEventListener("BankAdminIdChanged", handleBankAdminIdChange);
    return () => window.removeEventListener("BankAdminIdChanged", handleBankAdminIdChange);
  }, []);

  const getDocumentTypes = async () => {
    try {
      const res = await _NewapplicationService.getAllDropdownDataByName("documenttyepes");
      if (res.data) {
        setDocumentTypeOptions(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getSponsorFacilityList = async () => {
    try {
      const res = isFirmAdmin
        ? await _BankAdminService.getSponserListByEmailAndBankAdminId(loginEmail, BankAdminId)
        : await _BankAdminService.getSponserDropdownListByBankerIdV1(BankerId, roleId, isBanker);
      if (res?.data?.IsSuccess && res.data.Obj) {
        setSponsorFacilities(res.data.Obj);
      }
    } catch (error) {
      console.error("Error fetching sponsor facility list:", error);
    }
  };

  // Narrowing dropdowns (banker-side screen only) -- optional, populated up front rather than
  // gating the list behind a required selection (Requirement #7).
  const getFirmList = async () => {
    try {
      const res = await _BankAdminService.getFirmDropdownListByBankerIdV1(BankerId, roleId, isBanker);
      if (res?.data?.IsSuccess && res.data.Obj) {
        setFirmList(res.data.Obj);
      }
    } catch (error) {
      console.error("Error fetching firm list:", error);
    }
  };

  const fetchFacilityList = async (firmId) => {
    try {
      const res = await _BankAdminService.getLoanProgrameTypeListByFirmId(firmId, { bankerId: BankerId, roleId, isBanker });
      setFacilityList(res?.data?.IsSuccess && res.data.Obj ? res.data.Obj : []);
    } catch (error) {
      console.error("Error fetching loan programe type list:", error);
      setFacilityList([]);
    }
  };

  useEffect(() => {
    getDocumentTypes();
    if (!isFirmAdmin) {
      getFirmList();
    }
    if (BankerId && BankAdminId) {
      getSponsorFacilityList();
    } else if (isFirmAdmin && loginEmail) {
      getSponsorFacilityList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!narrowFirmId) {
      setFacilityList([]);
      setNarrowSponserId(undefined);
      return;
    }
    fetchFacilityList(narrowFirmId);
    setNarrowSponserId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrowFirmId]);

  const fetchCycles = async (page = pageIndex, chipValue = chip) => {
    try {
      setLoading(true);
      const reqObj = {
        IsFirmAdmin: isFirmAdmin,
        FirmAdminEmail: isFirmAdmin ? loginEmail : null,
        UserId: isFirmAdmin ? null : (BankerId ? Number(BankerId) : null),
        RoleId: isFirmAdmin ? null : roleId,
        Chip: chipValue,
        FirmId: narrowFirmId || null,
        SponserId: narrowSponserId || null,
        PageIndex: page,
        PageSize: pageSize,
      };
      const res = await _ReportingService.getCyclesForUser(reqObj);
      if (res?.data?.IsSuccess && res.data.Obj) {
        setRows(res.data.Obj.Rows || []);
        setTotalRecords(res.data.Obj.TotalRecords || 0);
        setCounts({
          AllCount: res.data.Obj.AllCount || 0,
          NeedsActionCount: res.data.Obj.NeedsActionCount || 0,
          OverdueCount: res.data.Obj.OverdueCount || 0,
          FlaggedCount: res.data.Obj.FlaggedCount || 0,
        });
      } else {
        setRows([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching reporting cycles:", error);
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFirmAdmin ? !!loginEmail : !!BankerId) {
      fetchCycles(pageIndex, chip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, chip, narrowFirmId, narrowSponserId]);

  const handleChipChange = (key) => {
    setChip(key);
    setPageIndex(1);
    closePanel();
  };

  const flaggedFacilityWarning = useMemo(() => {
    const flaggedNames = Array.from(
      new Set(rows.filter((r) => r.FailureFlag).map((r) => r.CreditFacilityName || r.FirmName))
    );
    if (flaggedNames.length === 0) return null;
    return `${flaggedNames[0]} has ${counts.FlaggedCount} unresolved reporting requirement failure${counts.FlaggedCount === 1 ? "" : "s"}.`;
  }, [rows, counts.FlaggedCount]);

  // ---- Action -> (label, style, handler) lookup, per mockup 3: nothing other than CycleState and
  // role should drive this. ----
  const getActionForRow = (row) => {
    if (!row.CanAct) {
      return { label: "View", type: "default" };
    }
    if (isFirmAdmin) {
      if (row.CycleState === "Upcoming" || row.CycleState === "Overdue") {
        return { label: "Submit", type: "primary" };
      }
      return { label: "View", type: "default" };
    }
    // Banker-family (Senior Private Banker / Underwriter Banker / Compliance Administrator)
    if (row.CycleState === "Upcoming" || row.CycleState === "Overdue") {
      return { label: "Remind", type: "default" };
    }
    // Separation of duties: whoever configured this requirement can't Verify or Review their own
    // cycle. Disabled directly on the Action column button (with a short explanation) rather than
    // only inside the panel, so it's clear before the extra click.
    const configuredByThisUser = row.RequirementCreatedBy && currentUserId && row.RequirementCreatedBy === currentUserId;
    const separationOfDutiesReason = "You configured this requirement — confirmation sits with another banker.";
    if (row.CycleState === "Pending") {
      return configuredByThisUser
        ? { label: "Verify", type: "primary", disabled: true, reason: separationOfDutiesReason }
        : { label: "Verify", type: "primary" };
    }
    if (row.CycleState === "Tested") {
      return configuredByThisUser
        ? { label: "Review", type: "primary", disabled: true, reason: separationOfDutiesReason }
        : { label: "Review", type: "primary" };
    }
    return { label: "View", type: "default" };
  };

  const closePanel = () => {
    setExpandedRow(null);
    setPanelMode(null);
    setSubmitFundValues({});
    setSubmitManualValue(undefined);
    setSubmitFile(null);
    setSubmitFileUrl(null);
    setSubmitFileSignedUrl(null);
    setVerifyMetricResults([]);
    setInstitutionActualValues({});
    setReviewComplianceStatus(undefined);
    setReviewComment("");
  };

  const openPanelForRow = async (row) => {
    if (expandedRow?.ReportingSubmissionId === row.ReportingSubmissionId) {
      closePanel();
      return;
    }
    closePanel();
    setExpandedRow(row);

    const action = getActionForRow(row);
    if (action.label === "Remind") {
      handleRemind(row);
      return;
    }
    if (action.label === "Submit") {
      setPanelMode("submit");
      return;
    }
    if (action.label === "Verify") {
      setPanelMode("verify");
      await loadMetricResultsForVerify(row);
      return;
    }
    if (action.label === "Review") {
      setPanelMode("review");
      await loadMetricResultsForVerify(row);
      return;
    }
    setPanelMode("view");
    await loadMetricResultsForVerify(row);
  };

  const handleRemind = async (row) => {
    try {
      setLoading(true);
      const res = await _ReportingService.sendManualReminder({
        ReportingSubmissionId: row.ReportingSubmissionId,
        SentByUserId: currentUserId || 0,
      });
      if (res?.data?.IsSuccess) {
        message.success("Reminder sent.");
      } else {
        message.error(res?.data?.Message || COMMON_ERROR_MSG);
      }
    } catch (error) {
      console.error("Error sending manual reminder:", error);
      message.error(COMMON_ERROR_MSG);
    } finally {
      setLoading(false);
      closePanel();
    }
  };

  const loadMetricResultsForVerify = async (row) => {
    if (row.TestedMetric === "N/A") {
      setVerifyMetricResults([]);
      return;
    }
    try {
      const res = await _ReportingService.getMetricResultsBySubmissionId(row.ReportingSubmissionId);
      const results = res?.data?.Obj || [];
      setVerifyMetricResults(results);
      const initial = {};
      results.forEach((r) => {
        initial[r.Id] = r.InstitutionActualValue ?? undefined;
      });
      setInstitutionActualValues(initial);
    } catch (error) {
      console.error("Error fetching metric results:", error);
      setVerifyMetricResults([]);
    }
  };

  // ---- Submit panel (Firm Administrator) -- reuses the exact same upload + record-submission
  // code path as the legacy Upload Document form (Requirement #4: one Submit action does both). ----
  const validateSubmitFile = (file) => {
    const isValidType = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);
    const isUnder25MB = file.size <= 25 * 1024 * 1024;
    if (!isValidType) {
      message.error("Only PDF/XLSX/DOCX files are allowed!");
      return false;
    }
    if (!isUnder25MB) {
      message.error("File size must be up to 25MB!");
      return false;
    }
    return true;
  };

  const beforeSubmitUpload = async (file) => {
    if (!validateSubmitFile(file)) {
      return Upload.LIST_IGNORE;
    }
    const docType = DOCUMENT_TYPE_FIELD_NAME_MAP[expandedRow?.DocumentTypeName] || "sponserreport_other";
    const rowId = getNextRowId();
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("files[]", file);
      const uploadRes = await _NewapplicationService.UploadSponserReportDocument(formData, docType, rowId, expandedRow.SponserId);
      if (!uploadRes?.data?.IsSucess || !uploadRes?.data?.URL) {
        message.error(uploadRes?.data?.Message || `${file.name} failed to upload.`);
        return Upload.LIST_IGNORE;
      }
      let relativePath = uploadRes.data.URL;
      try {
        relativePath = new URL(uploadRes.data.URL).pathname;
      } catch (e) { /* keep raw URL if it isn't a full absolute URL */ }
      setSubmitFile(file);
      setSubmitFileUrl(relativePath);
      setSubmitFileSignedUrl(uploadRes.data.URL);
      message.success(uploadRes?.data?.Message || `${file.name} uploaded successfully.`);
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error(COMMON_ERROR_MSG);
      return Upload.LIST_IGNORE;
    } finally {
      setSubmitting(false);
    }
    return false;
  };

  const facilityFundsForSubmit = useMemo(() => {
    if (!expandedRow) return [];
    const sponsor = sponsorFacilities.find((s) => s.SponserId === expandedRow.SponserId);
    return sponsor?.listsponserFundModel || [];
  }, [expandedRow, sponsorFacilities]);

  const isFundLevelMetric = expandedRow && FUND_LEVEL_METRICS.includes(expandedRow.TestedMetric);
  const isManualValueMetric = expandedRow && MANUAL_VALUE_METRICS.includes(expandedRow.TestedMetric);

  const handleSubmitReport = async () => {
    if (!expandedRow) return;

    if (!submitFileUrl) {
      message.error("Please upload a file before submitting.");
      return;
    }
    if (isFundLevelMetric && facilityFundsForSubmit.some((f) => !String(submitFundValues[f.Id] || "").trim())) {
      message.error(`Please enter ${expandedRow.TestedMetric} for every fund.`);
      return;
    }
    if (isManualValueMetric && (submitManualValue === undefined || submitManualValue === null)) {
      message.error(`Please enter the Firm Stated ${expandedRow.TestedMetric} value.`);
      return;
    }

    try {
      setSubmitting(true);
      const documentTypeId = documentTypeOptions.find((t) => t.Name === expandedRow.DocumentTypeName)?.Id;
      const fundIds = facilityFundsForSubmit.map((f) => f.Id).join(",");

      const payload = {
        Id: 0,
        SponserId: expandedRow.SponserId,
        FirmId: expandedRow.FirmId,
        LoanProgrameTypeId: expandedRow.LoanProgrameTypeId,
        BankerId: BankerId ? parseInt(BankerId) : null,
        DocumentPath: submitFileUrl,
        DocumentTypeId: documentTypeId ?? null,
        DocumentName: `${expandedRow.DocumentTypeName || "Document"} - ${moment().format("MM/DD/YYYY")}`,
        Funds: fundIds,
        FirmAdminCreatedBy: isFirmAdmin ? (firmAdminApplicantId ? parseInt(firmAdminApplicantId) : 0) : 0,
        CreatedBy: isFirmAdmin ? 0 : BankerId,
        ...(isFundLevelMetric
          ? {
              listsponserFundModel: facilityFundsForSubmit.map((f) => ({
                Id: f.Id,
                SponserId: f.SponserId,
                FundName: f.FundName,
                IRR: expandedRow.TestedMetric === "IRR" ? submitFundValues[f.Id] : f.IRR,
                MOIC: expandedRow.TestedMetric === "MOIC" ? submitFundValues[f.Id] : f.MOIC,
              })),
            }
          : {}),
      };

      const saveRes = await _BankAdminService.addSponserReportDucument(payload);
      if (!saveRes?.data?.IsSuccess) {
        message.error(saveRes?.data?.Message || COMMON_ERROR_MSG);
        return;
      }

      try {
        const submissionRes = await _ReportingService.recordReportingSubmission({
          SponserId: expandedRow.SponserId,
          DocumentTypeId: documentTypeId,
          SubmittedBy: BankerId ? parseInt(BankerId) : null,
          ManualActualValue: isManualValueMetric ? submitManualValue : null,
        });
        if (!submissionRes?.data?.IsSuccess || !submissionRes?.data?.Obj) {
          message.warning(submissionRes?.data?.Message || "Document uploaded, but reporting requirement evaluation failed.");
        } else {
          message.success("Report submitted successfully.");
        }
      } catch (submissionError) {
        console.error("Error recording reporting submission:", submissionError);
        message.warning("Document uploaded, but reporting requirement evaluation failed.");
      }

      closePanel();
      await fetchCycles(pageIndex, chip);
    } catch (error) {
      console.error("Error submitting report:", error);
      message.error(COMMON_ERROR_MSG);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Verify panel (Banker/Compliance) ----
  const handleSaveAndTest = async () => {
    if (!expandedRow) return;
    const entries = verifyMetricResults.map((r) => ({
      FundId: r.FundId || null,
      InstitutionActualValue: institutionActualValues[r.Id],
    }));
    if (entries.some((e) => e.InstitutionActualValue === undefined || e.InstitutionActualValue === null || e.InstitutionActualValue === "")) {
      message.error("Please enter the Institution Actual Value for every fund.");
      return;
    }
    try {
      setVerifying(true);
      const res = await _ReportingService.setInstitutionActualValue({
        ReportingSubmissionId: expandedRow.ReportingSubmissionId,
        EnteredBy: currentUserId,
        Entries: entries,
      });
      if (res?.data?.IsSuccess) {
        message.success("Institution Actual Value saved.");
        closePanel();
        await fetchCycles(pageIndex, chip);
      } else {
        message.error(res?.data?.Message || COMMON_ERROR_MSG);
      }
    } catch (error) {
      console.error("Error saving institution actual value:", error);
      message.error(COMMON_ERROR_MSG);
    } finally {
      setVerifying(false);
    }
  };

  // ---- Review panel (Banker/Compliance) ----
  const canReviewThisRow = expandedRow && !(expandedRow.RequirementCreatedBy && currentUserId && expandedRow.RequirementCreatedBy === currentUserId);

  const handleSaveReview = async () => {
    if (!expandedRow) return;
    if (!reviewComplianceStatus) {
      message.error("Please select Compliant, Not Compliant or Waived.");
      return;
    }
    if (reviewComplianceStatus !== "Compliant" && !reviewComment.trim()) {
      message.error("A comment is required when marking Not Compliant or Waived.");
      return;
    }
    try {
      setReviewing(true);
      const res = await _ReportingService.setComplianceStatus({
        ReportingSubmissionId: expandedRow.ReportingSubmissionId,
        ComplianceStatus: reviewComplianceStatus,
        ComplianceComment: reviewComment,
        ReviewedBy: currentUserId,
      });
      if (res?.data?.IsSuccess) {
        message.success("Reviewed.");
        closePanel();
        await fetchCycles(pageIndex, chip);
      } else {
        message.error(res?.data?.Message || COMMON_ERROR_MSG);
      }
    } catch (error) {
      console.error("Error saving review:", error);
      message.error(COMMON_ERROR_MSG);
    } finally {
      setReviewing(false);
    }
  };

  const handleDownloadCovenantResults = async () => {
    if (!narrowSponserId && !firmList.length) {
      message.error("Nothing to export.");
      return;
    }
    try {
      setLoading(true);
      const targetSponserId = narrowSponserId || rows[0]?.SponserId;
      if (!targetSponserId) {
        message.error("Select a Credit Facility to export, or narrow the list first.");
        return;
      }
      const res = await _ReportingService.exportCovenantResultsBySponserId(targetSponserId);
      if (res?.data?.IsSuccess && res.data.Obj) {
        window.open(res.data.Obj, "_blank");
      } else {
        message.error(res?.data?.Message || COMMON_ERROR_MSG);
      }
    } catch (error) {
      console.error("Error exporting covenant results:", error);
      message.error(COMMON_ERROR_MSG);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
    const cols = [];
    if (!isFirmAdmin) {
      cols.push({ title: "Firm", dataIndex: "FirmName", render: (v) => v || "-" });
    }
    cols.push({ title: "Credit Facility", dataIndex: "CreditFacilityName", render: (v) => v || "-" });
    cols.push({ title: "Document", dataIndex: "DocumentTypeName", render: (v) => v || "-" });
    cols.push({ title: "Due Date", dataIndex: "DueDate", render: (v) => (v ? moment(v).format("M/D/YYYY") : "-") });

    if (isFirmAdmin) {
      cols.push({ title: "Required", dataIndex: "RequiredDisplay" });
      cols.push({
        title: "Status",
        dataIndex: "CycleState",
        render: (v) => <Tag color={STATE_TAG_COLOR[v] || "default"}>{v}</Tag>,
      });
    } else {
      cols.push({
        title: "Stated / Actual",
        key: "statedActual",
        render: (_, row) =>
          row.FundCount > 1
            ? `${row.FundCount} funds`
            : `${row.FirmStatedValue ?? "-"} / ${row.InstitutionActualValue ?? "-"}`,
      });
      cols.push({
        title: "Result",
        key: "result",
        render: (_, row) => {
          if (row.CycleState === "Closed") {
            return (
              <>
                <Tag color={row.SystemResult === "Fail" ? "red" : "green"}>{row.SystemResult}</Tag>
                <Tag color="blue">Submitted</Tag>
              </>
            );
          }
          if (row.CycleState === "Tested") {
            return <Tag color={row.SystemResult === "Fail" ? "red" : "green"}>{row.SystemResult}</Tag>;
          }
          return <Tag color={STATE_TAG_COLOR[row.CycleState] || "default"}>{row.CycleState}</Tag>;
        },
      });
    }

    cols.push({
      title: "Action",
      key: "action",
      render: (_, row) => {
        const action = getActionForRow(row);
        const button = (
          <Button
            type={action.type}
            size="small"
            disabled={action.disabled}
            onClick={() => !action.disabled && openPanelForRow(row)}
          >
            {action.label === "Remind" && <BellOutlined style={{ marginRight: 4 }} />}
            {action.label}
          </Button>
        );
        return action.reason ? <Tooltip title={action.reason}>{button}</Tooltip> : button;
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirmAdmin, rows, expandedRow]);

  return (
    <Spin spinning={loading}>
      <SidebarLayout>
        <div className="reports-wrapper">
          <div className="reports-header">
            <div className="reports-header-row">
              <div>
                <Title level={4} className="reports-title">Reports</Title>
                <Text className="reports-subtitle">
                  {isFirmAdmin
                    ? "Upload and manage compliance documents for your firm and facilities."
                    : "Reporting requirements on facilities you are assigned to."}
                </Text>
              </div>
              {!isFirmAdmin && canAct && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  className="reports-download-btn"
                  onClick={handleDownloadCovenantResults}
                >
                  Download Covenant Results
                </Button>
              )}
            </div>
          </div>

          {flaggedFacilityWarning && (
            <Alert type="error" showIcon message={flaggedFacilityWarning} style={{ marginBottom: 16 }} />
          )}

          <div className="reports-chip-bar" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {CHIP_OPTIONS.map((c) => (
              <Button
                key={c.key}
                type={chip === c.key ? "primary" : "default"}
                shape="round"
                size="small"
                onClick={() => handleChipChange(c.key)}
              >
                {c.label} {c.key === "All" ? counts.AllCount : c.key === "NeedsAction" ? counts.NeedsActionCount : counts.OverdueCount}
              </Button>
            ))}
            {!isFirmAdmin && (
              <Button
                type={chip === "Flagged" ? "primary" : "default"}
                shape="round"
                size="small"
                onClick={() => handleChipChange("Flagged")}
              >
                Flagged {counts.FlaggedCount}
              </Button>
            )}

            {!isFirmAdmin && (
              <div className="reports-narrow-filters">
                <Select
                  placeholder="Firm: All"
                  allowClear
                  bordered={false}
                  size="small"
                  className="reports-filter-select"
                  dropdownMatchSelectWidth={false}
                  style={{ width: 160 }}
                  value={narrowFirmId}
                  onChange={(v) => { setNarrowFirmId(v); setPageIndex(1); }}
                >
                  {firmList.map((firm) => (
                    <Option key={firm.Id} value={firm.Id}>{firm.FirmName}</Option>
                  ))}
                </Select>
                <span className="reports-filter-sep">·</span>
                <Select
                  placeholder="Credit Facility: All"
                  allowClear
                  bordered={false}
                  size="small"
                  className="reports-filter-select"
                  dropdownMatchSelectWidth={false}
                  style={{ width: 310 }}
                  value={narrowSponserId}
                  onChange={(v) => { setNarrowSponserId(v); setPageIndex(1); }}
                  disabled={!narrowFirmId}
                >
                  {facilityList.map((f) => (
                    <Option key={f.SponserId} value={f.SponserId}>{f.Name}</Option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <div className="compliance-table-wrap">
            <Table
              columns={columns}
              dataSource={rows}
              rowKey="ReportingSubmissionId"
              pagination={false}
              locale={{ emptyText: "No Data." }}
              scroll={{ x: "max-content" }}
            />
            <div className="table-footer" style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Pagination
                className="pagination"
                current={pageIndex}
                pageSize={pageSize}
                total={totalRecords}
                onChange={(page) => setPageIndex(page)}
                showSizeChanger={false}
              />
            </div>
          </div>

          {expandedRow && panelMode && (
            <div className="compliance-upload-card" style={{ marginTop: 16 }}>
              <div className="card-title">
                {panelMode === "submit" && `Submit — ${expandedRow.DocumentTypeName}`}
                {panelMode === "verify" && `Verify — ${expandedRow.DocumentTypeName}`}
                {panelMode === "review" && `Review — ${expandedRow.DocumentTypeName}`}
                {panelMode === "view" && `${expandedRow.DocumentTypeName}`}
              </div>
              <Text className="table-footer-text" style={{ display: "block", marginBottom: 16 }}>
                {expandedRow.CreditFacilityName} · due {moment(expandedRow.DueDate).format("M/D/YYYY")}
                {expandedRow.RequiredDisplay && expandedRow.RequiredDisplay !== "--" ? ` · ${expandedRow.RequiredDisplay}` : ""}
                {expandedRow.SubmissionDate ? ` · submitted ${moment(expandedRow.SubmissionDate).format("M/D/YYYY")}` : ""}
              </Text>

              {panelMode === "view" && (expandedRow.SystemResult || expandedRow.ComplianceStatus) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", gap: 24 }}>
                    {expandedRow.SystemResult && (
                      <Text>System Result: <Tag color={expandedRow.SystemResult === "Fail" ? "red" : "green"}>{expandedRow.SystemResult}</Tag></Text>
                    )}
                    {expandedRow.ComplianceStatus && (
                      <>
                        <Text>Internal Status: <Tag>{expandedRow.ComplianceStatus}</Tag></Text>
                        <Text>Action: <Tag color="blue">Submitted</Tag></Text>
                      </>
                    )}
                  </div>
                  {expandedRow.ComplianceStatus && expandedRow.ComplianceComment && (
                    <Text style={{ display: "block", marginTop: 8 }}>
                      <Text strong>Comment: </Text>{expandedRow.ComplianceComment}
                    </Text>
                  )}
                </div>
              )}

              {panelMode === "submit" && (
                <>
                  {isFundLevelMetric && (
                    <div style={{ marginBottom: 16 }}>
                      {facilityFundsForSubmit.length === 0 ? (
                        <Text>No funds available for this facility.</Text>
                      ) : (
                        <Table
                          rowKey="Id"
                          size="small"
                          pagination={false}
                          tableLayout="fixed"
                          dataSource={facilityFundsForSubmit}
                          columns={[
                            { title: "Fund", dataIndex: "FundName", width: "30%" },
                            {
                              title: expandedRow.TestedMetric,
                              key: "value",
                              width: "30%",
                              align: "center",
                              render: (_, fund) => (
                                <Input
                                  placeholder={`Enter ${expandedRow.TestedMetric}`}
                                  value={submitFundValues[fund.Id] || ""}
                                  onChange={(e) => setSubmitFundValues((prev) => ({ ...prev, [fund.Id]: e.target.value }))}
                                  style={{ width: 250 }}
                                />
                              ),
                            },
                            { title: "Required", key: "required", width: "30%", align: "center", render: () => expandedRow.RequiredDisplay },
                          ]}
                        />
                      )}
                    </div>
                  )}
                  {isManualValueMetric && (
                    <div style={{ marginBottom: 16 }}>
                      <InputNumber
                        placeholder={`Enter ${expandedRow.TestedMetric}`}
                        value={submitManualValue}
                        onChange={setSubmitManualValue}
                        min={0}
                        max={expandedRow.TestedMetric === "LTV" ? 100 : undefined}
                        step={0.1}
                        style={{ width: 200 }}
                      />
                    </div>
                  )}
                  <Dragger
                    beforeUpload={beforeSubmitUpload}
                    onRemove={() => { setSubmitFile(null); setSubmitFileUrl(null); setSubmitFileSignedUrl(null); }}
                    fileList={submitFile ? [{ uid: "-1", name: submitFile.name, status: "done" }] : []}
                    maxCount={1}
                    showUploadList={false}
                    accept=".pdf,.xlsx,.docx"
                    className="upload-dragger"
                  >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Drag and drop your file here</p>
                    <p className="ant-upload-hint">or</p>
                    <Button icon={<UploadOutlined />}>{submitFile ? "Replace File" : "Upload File"}</Button>
                  </Dragger>
                  <div className="report-upload-format-text">Format: pdf, xlsx, docx &nbsp;&nbsp; Max Size: 25MB</div>
                  {submitFile && submitFileUrl && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 8,
                        padding: "8px 12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                      }}
                    >
                      <span>
                        <PaperClipOutlined style={{ marginRight: 8 }} />
                        <a
                          href={submitFileSignedUrl || (submitFileUrl.startsWith("http") ? submitFileUrl : `${APP_BLOB_ROOT}${submitFileUrl}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {submitFile.name}
                        </a>
                      </span>
                      <DeleteOutlined
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          Modal.confirm({
                            title: `Remove "${submitFile.name}"?`,
                            okText: "Yes",
                            cancelText: "No",
                            onOk() {
                              setSubmitFile(null);
                              setSubmitFileUrl(null);
                              setSubmitFileSignedUrl(null);
                            },
                          });
                        }}
                      />
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <Button type="primary" loading={submitting} onClick={handleSubmitReport}>Submit Report</Button>
                  </div>
                </>
              )}

              {(panelMode === "verify" || panelMode === "review" || panelMode === "view") && (
                <>
                  {expandedRow.TestedMetric === "N/A" ? (
                    <Text>No metric test applies — result is based on whether the document was submitted by its due date.</Text>
                  ) : (
                    <Table
                      rowKey="Id"
                      size="small"
                      pagination={false}
                      dataSource={verifyMetricResults}
                      columns={[
                        { title: "Tested on", dataIndex: "FundName", render: (v) => v || "Facility" },
                        {
                          title: "Firm Stated",
                          dataIndex: "FirmStatedValue",
                          render: (v) => v ?? "--",
                        },
                        {
                          title: "Institution Actual",
                          key: "actual",
                          render: (_, r) =>
                            panelMode === "verify" && canAct ? (
                              <InputNumber
                                size="small"
                                style={{ width: 100 }}
                                value={institutionActualValues[r.Id]}
                                onChange={(v) => setInstitutionActualValues((prev) => ({ ...prev, [r.Id]: v }))}
                                disabled={!canReviewThisRow}
                              />
                            ) : (
                              r.InstitutionActualValue ?? "Not Entered"
                            ),
                        },
                        { title: "Required", dataIndex: "ThresholdValue", render: (_, r) => `${r.Operator || ""} ${r.ThresholdValue ?? ""}` },
                      ]}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {expandedRow.UploadedDocumentPath && (
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Uploaded Document: </Text>
                      <a
                        href={expandedRow.UploadedDocumentPath.startsWith("http") ? expandedRow.UploadedDocumentPath : `${APP_BLOB_ROOT}${expandedRow.UploadedDocumentPath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {expandedRow.UploadedDocumentName || "View Document"}
                      </a>
                      <Text type="secondary" style={{ display: "block" }}>Submitted by the Firm Administrator — view only.</Text>
                    </div>
                  )}

                  {panelMode === "verify" && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      {!canReviewThisRow && (
                        <Text type="secondary">You configured this requirement — confirmation sits with another banker.</Text>
                      )}
                      <Button type="primary" loading={verifying} disabled={!canReviewThisRow} onClick={handleSaveAndTest} style={{ marginLeft: "auto" }}>
                        Save &amp; Test
                      </Button>
                    </div>
                  )}

                  {panelMode === "review" && (
                    <>
                      <div style={{ marginTop: 16 }}>
                        <Text strong>Internal Status</Text>
                        <div style={{ marginTop: 8 }}>
                          <Radio.Group
                            value={reviewComplianceStatus}
                            onChange={(e) => setReviewComplianceStatus(e.target.value)}
                            disabled={!canReviewThisRow}
                          >
                            <Radio.Button value="Compliant">Compliant</Radio.Button>
                            <Radio.Button value="Not Compliant">Not Compliant</Radio.Button>
                            <Radio.Button value="Waived">Waived</Radio.Button>
                          </Radio.Group>
                        </div>
                      </div>
                      <div style={{ marginTop: 16 }}>
                        <Text strong>Comment</Text>
                        <TextArea
                          rows={3}
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          disabled={!canReviewThisRow}
                          style={{ marginTop: 8 }}
                        />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                        {!canReviewThisRow && (
                          <Text type="secondary">You configured this requirement — confirmation sits with another banker.</Text>
                        )}
                        <Button type="primary" loading={reviewing} disabled={!canReviewThisRow} onClick={handleSaveReview} style={{ marginLeft: "auto" }}>
                          Save
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </SidebarLayout>
    </Spin>
  );
};

export default NewReportsPage;
